import { useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { logger } from '@/lib/logger'

import { useBranchStore } from '@/stores/useBranchStore'

import toast from '@/components/toast'

/**
 * BranchChangeHandler
 * Listens for active branch changes in the global store and triggers a safe, app-wide data reload.
 *
 * What it does on change:
 * - Cancels in-flight queries to avoid mixing responses across branches
 * - Resets (clears state + marks stale) all queries so they refetch with the new X-Branch-ID header
 * - Shows a small toast message for UX feedback
 */
export default function BranchChangeHandler() {
  const queryClient = useQueryClient()
  const activeBranch = useBranchStore((s) => s.activeBranch)
  const prevBranchIdRef = useRef<string | number | null>(null)

  useEffect(() => {
    const currentId = activeBranch?.id ?? null
    const prevId = prevBranchIdRef.current

    // Skip on first mount
    if (prevId === null) {
      prevBranchIdRef.current = currentId
      return
    }

    // No change
    if (prevId === currentId) return

    // Update prev immediately to avoid double-firing
    prevBranchIdRef.current = currentId

    const name = activeBranch?.name ?? String(currentId ?? '')
    toast.info(`Switching to ${name}…`)

    // Cancel and reset all queries to refetch with new header
    ;(async () => {
      try {
        await queryClient.cancelQueries()
        await queryClient.resetQueries()
      } catch (e) {
        // Non-fatal; keep UX smooth
        logger.warn('Branch change reload failed', e)
      }
    })()
  }, [activeBranch, queryClient])

  return null
}
